/**
 * @file     SeoAiChatBlock.tsx
 * @purpose  Reusable hero-embedded Relta conversation for allowlisted SEO
 *           surfaces. Page families provide their own server-rendered context
 *           copy while the server maps the surface id to trusted instructions.
 */
'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import SvgIcon from '@mui/material/SvgIcon';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useReltaChat } from '@/features/ai-chat/useReltaChat';
import { ChatWorkspace } from '@/features/ai-chat/components/ChatWorkspace';
import {
  seoChatSurfaceEnabled,
  type SeoChatSurfaceId,
} from '@/services/ai-chat/seoChatSurface';

const AuthGateModal = dynamic(
  () => import('@/components/auth/AuthGateModal').then((module) => module.AuthGateModal),
  { ssr: false },
);

export interface SeoAiChatCopy {
  sectionLabel: string;
  headerSub: string;
  welcome: string;
  suggestions: string[];
  placeholder: string;
  inputLabel: string;
}

export interface SeoAiChatBlockProps {
  surface: SeoChatSurfaceId;
  /** Closed-vocabulary page locator. The API resolves it through the server's
   * BC city registry before it reaches the model context. */
  surfaceLocation?: string;
  /** Optional closed-vocabulary home-search facet. The server accepts only
   * slugs from its mirrored facet allowlist. */
  surfaceFacet?: string;
  redirectTo: string;
  copy: SeoAiChatCopy;
  /** Existing deterministic conversion path shown when this family flag is
   * off, so a staged rollout never removes the page's current utility. */
  fallback?: React.ReactNode;
  /**
   * Keeps the assistant subordinate to the page's deterministic answer. The
   * conversation expands in place only after the visitor asks for it.
   */
  compact?: boolean;
}

function AskIcon() {
  return (
    <SvgIcon fontSize="small" aria-hidden>
      <path d="M4 4h16v12H8l-4 4V4Zm2 2v9.2L7.2 14H18V6H6Zm3 3h6v2H9V9Z" />
    </SvgIcon>
  );
}

/**
 * Renders nothing (or the deterministic fallback) when the surface is off, and
 * does so before any chat hook runs. A layout slot that receives this element
 * still reserves its column or margin, so callers gate the slot itself with
 * `seoChatSurfaceEnabled` from services/ai-chat/seoChatSurface.
 */
export function SeoAiChatBlock(props: SeoAiChatBlockProps) {
  if (!seoChatSurfaceEnabled(props.surface)) return props.fallback ?? null;
  return <SeoAiChatBlockEnabled {...props} />;
}

function SeoAiChatBlockEnabled({
  surface,
  surfaceLocation,
  surfaceFacet,
  redirectTo,
  copy,
  compact = false,
}: SeoAiChatBlockProps) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const conversationScope = [
    'seo',
    redirectTo,
  ].join(':');
  const chat = useReltaChat({
    surface,
    surfaceLocation,
    surfaceFacet,
    conversationScope,
    serverHistory: false,
    anonymousTurnLimit: 3,
  });
  const c = theme.semanticColors;
  const [expanded, setExpanded] = useState(!compact);

  if (!expanded) {
    return (
      <ButtonBase
        component="button"
        type="button"
        onClick={() => setExpanded(true)}
        aria-expanded="false"
        aria-label={copy.sectionLabel}
        sx={{
          width: '100%',
          minHeight: 76,
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          textAlign: 'left',
          borderRadius: `${theme.layout.radius.md}px`,
          border: `1px solid ${c.borderSubtle}`,
          backgroundColor: c.surfaceCard,
          color: c.textPrimary,
          transition: 'border-color 180ms ease, background-color 180ms ease',
          '&:hover': { borderColor: c.interactivePrimary, backgroundColor: c.surfaceElevated },
          '&:focus-visible': { outline: `2px solid ${c.interactivePrimary}`, outlineOffset: 3 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: `${theme.layout.radius.sm}px`,
              color: c.interactivePrimary,
              backgroundColor: c.surfaceElevated,
            }}
          >
            <AskIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: c.interactivePrimary }}>
              {t('seoAi.label', 'Ask Relta')}
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: c.textSecondary, whiteSpace: { sm: 'nowrap' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {copy.headerSub}
            </Typography>
          </Box>
        </Box>
        <Typography component="span" sx={{ flexShrink: 0, fontSize: 13, fontWeight: 600, color: c.interactivePrimary }}>
          {t('seoAi.open', 'Open')}
        </Typography>
      </ButtonBase>
    );
  }

  return (
    <Box
      component="section"
      aria-label={copy.sectionLabel}
      sx={{
        height: { xs: 510, sm: 540, lg: 560 },
        minHeight: 0,
        overflow: 'hidden',
        borderRadius: `${theme.layout.radius.lg}px`,
        border: `1px solid ${c.borderSubtle}`,
        backgroundColor: c.surfaceCard,
      }}
    >
      <Box
        sx={{
          minHeight: 58,
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${c.borderSubtle}`,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: c.interactivePrimary }}>
            {t('seoAi.label', 'Ask Relta')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: c.textSecondary }}>
            {copy.headerSub}
          </Typography>
        </Box>
        {compact ? (
          <ButtonBase
            component="button"
            type="button"
            onClick={() => setExpanded(false)}
            aria-label={t('seoAi.closeLabel', 'Close Ask Relta')}
            sx={{ ml: 'auto', minWidth: 44, minHeight: 44, borderRadius: `${theme.layout.radius.md}px`, color: c.textSecondary }}
          >
            {t('seoAi.close', 'Close')}
          </ButtonBase>
        ) : null}
      </Box>

      <Box sx={{ height: 'calc(100% - 58px)', minHeight: 0 }}>
        <ChatWorkspace
          chat={chat}
          embedded
          welcomeMessage={copy.welcome}
          composerSuggestions={copy.suggestions}
          autoFocusComposer={false}
          composerPlaceholder={copy.placeholder}
          composerAriaLabel={copy.inputLabel}
          gateNote={t(
            'seoAi.buyingCosts.gateNote',
            'Create an account to keep this conversation and continue asking questions.',
          )}
          gateCta={t('seoAi.buyingCosts.gateCta', 'Keep chatting')}
          renderGate={(open, onClose) => (
            <AuthGateModal
              open={open}
              redirectTo={redirectTo}
              onDismiss={onClose}
              signupTitle={t('seoAi.buyingCosts.signupTitle', 'Keep your conversation')}
              signupSubtitle={t(
                'seoAi.buyingCosts.signupSubtitle',
                'Create an account to continue with Relta and return to your conversation.',
              )}
            />
          )}
        />
      </Box>
    </Box>
  );
}
